'use client';

import type { FormEvent, InputHTMLAttributes, ReactNode } from 'react';
import { useCallback, useRef } from 'react';
import type { IconName } from '../../packages/icons/icon-registry';
import type { InputSize } from './input';
import { Input } from './input';
import { Label } from './label';
import { Stack } from './stack';
import { Text } from './text';

export interface FormProps {
  id?: string;
  ariaLabel?: string;
  className?: string;
  children: ReactNode;
  onSubmit?: (payload: {
    values: Record<string, FormDataEntryValue | FormDataEntryValue[]>;
    event: FormEvent<HTMLFormElement>;
    form: HTMLFormElement;
  }) => void;
  preventDefault?: boolean;
  noValidate?: boolean;
}

function formDataToObject(fd: FormData): Record<string, FormDataEntryValue | FormDataEntryValue[]> {
  const out: Record<string, FormDataEntryValue | FormDataEntryValue[]> = {};
  for (const [key, value] of fd.entries()) {
    const existing = out[key];
    if (existing === undefined) {
      out[key] = value;
      continue;
    }
    if (Array.isArray(existing)) {
      existing.push(value);
      continue;
    }
    out[key] = [existing, value];
  }
  return out;
}

export function Form({
  id,
  ariaLabel,
  className,
  children,
  onSubmit,
  preventDefault = true,
  noValidate = true,
}: FormProps) {
  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      if (preventDefault) event.preventDefault();
      if (!onSubmit) return;

      const fd = new FormData(event.currentTarget);
      onSubmit({
        values: formDataToObject(fd),
        event,
        form: event.currentTarget,
      });
    },
    [onSubmit, preventDefault],
  );

  return (
    <form
      id={id}
      className={className}
      aria-label={ariaLabel}
      noValidate={noValidate}
      onSubmit={handleSubmit}
      data-form-id={id}
    >
      {children}
    </form>
  );
}

Form.displayName = 'Form';

export interface FormFieldProps {
  id?: string;
  name: string;
  label: string;
  hideLabel?: boolean;
  size?: InputSize;
  type?: 'text' | 'email' | 'password' | 'number' | 'textarea' | 'select' | 'switch' | 'checkbox';
  secure?: boolean;
  multiline?: boolean;
  placeholder?: string;
  helperText?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  value?: string | number | boolean;
  defaultValue?: string | number | boolean;
  onChange?: (value: string | number | boolean) => void;
  icon?: IconName | string | null;
  iconPosition?: 'left' | 'right';
  iconSize?: number;
  autoComplete?: InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  minLength?: number;
  maxLength?: number;
  className?: string;
  children?: ReactNode;
}

function coerceBoolean(value: unknown, defaultValue: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off') return false;
  }
  if (typeof value === 'number') return value !== 0;
  return defaultValue;
}

export function FormField({
  id,
  name,
  label,
  hideLabel = false,
  size = 'md',
  type = 'text',
  secure = false,
  placeholder,
  helperText,
  error,
  required = false,
  disabled = false,
  value,
  defaultValue,
  onChange,
  icon,
  iconPosition = 'left',
  iconSize = 20,
  autoComplete,
  inputMode,
  minLength,
  maxLength,
  multiline = false,
  className,
  children,
}: FormFieldProps) {
  const fieldId = id ?? `field-${name}`;
  const errorId = `${fieldId}__error`;
  const helperId = `${fieldId}__helper`;
  const controlIsValid = !error;
  const secureBool = coerceBoolean(secure, false);
  const isTextarea = coerceBoolean(multiline, false) || type === 'textarea';
  const effectiveType = secureBool ? 'password' : type;

  // The controlled/uncontrolled decision must be stable for the lifetime of
  // the element. Once a value binding has been present, a later undefined
  // value is coerced to an empty string instead of flipping the control back
  // to uncontrolled (which React treats as an error).
  const wasControlledRef = useRef(false);
  if (value !== undefined) wasControlledRef.current = true;
  const hasControlledValue = wasControlledRef.current;

  return (
    <Stack gap="sm" {...(className ? { className } : {})}>
      {hideLabel ? (
        <label htmlFor={fieldId} className="sr-only">
          {label}
        </label>
      ) : (
        <Label
          htmlFor={fieldId}
          required={required}
          helperText={!error ? helperText : undefined}
        >
          {label}
        </Label>
      )}

      {children ? (
        <div>{children}</div>
      ) : isTextarea ? (
        <textarea
          id={fieldId}
          name={name}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          {...(hasControlledValue
            ? { value: String(value ?? '') }
            : defaultValue !== undefined
              ? { defaultValue: String(defaultValue ?? '') }
              : {})}
          onChange={(e) => onChange?.(e.target.value)}
          aria-invalid={!controlIsValid || undefined}
          aria-describedby={error ? errorId : helperText ? helperId : undefined}
          autoComplete={autoComplete}
          inputMode={inputMode}
          {...(typeof minLength === 'number' ? { minLength } : {})}
          {...(typeof maxLength === 'number' ? { maxLength } : {})}
          className="block w-full rounded-xl border border-secondary_alt/80 bg-primary shadow-xs transition-all placeholder:text-tertiary focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10 disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-secondary text-secondary"
          rows={4}
        />
      ) : (
        <Input
          id={fieldId}
          name={name}
          type={effectiveType}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          size={size}
          icon={icon ?? null}
          iconPosition={iconPosition}
          iconSize={iconSize}
          isValid={controlIsValid}
          {...(hasControlledValue
            ? { value: String(value ?? '') }
            : defaultValue !== undefined
              ? { defaultValue: String(defaultValue ?? '') }
              : {})}
          onChange={(e) => onChange?.(e.target.value)}
          aria-describedby={error ? errorId : helperText ? helperId : undefined}
          autoComplete={autoComplete}
          inputMode={inputMode}
          {...(typeof minLength === 'number' ? { minLength } : {})}
          {...(typeof maxLength === 'number' ? { maxLength } : {})}
        />
      )}

      {error ? (
        <Text id={errorId} size="text-sm" tone="primary" className="text-danger" role="alert">
          {error}
        </Text>
      ) : null}

      {!error && !hideLabel && helperText ? (
        <span id={helperId} className="sr-only">
          {helperText}
        </span>
      ) : null}
    </Stack>
  );
}

FormField.displayName = 'FormField';